import { computed } from "vue";
import { useRoute } from "vue-router";
import { clientPageStore } from "@/store/client-page";
import { projectPageStore } from "@/store/project-page";

/**
 * Уровень доступа к блоку работ (поле JobAccess клиента/проекта):
 *   0 — доступ запрещён, 1 — только чтение, 2 — полный доступ.
 *
 * Доступ вычисляется напрямую из route.query + сторов страниц (openedClient /
 * openedProject), а НЕ из jobsStore.context. Это важно: контекст в jobsStore
 * выставляется внутри JobsPanelSearch, который при JobAccess === 0 скрывается и
 * перестаёт обновляться. Опора на route делает значение всегда актуальным и
 * реактивным независимо от того, смонтирован ли JobsPanelSearch.
 *
 * По умолчанию 2 (полный доступ): на главной ленте и пока сущность ещё грузится.
 */
export function useJobsAccess() {
	const route = useRoute();
	const clientStore = clientPageStore();
	const projectStore = projectPageStore();

	const jobAccessLevel = computed<number>(() => {
		const hasId = route.query.id != null && route.query.id !== "";
		if (route.query.page === "client" && hasId) {
			return clientStore.openedClient?.JobAccess ?? 2;
		}
		if (route.query.page === "project" && hasId) {
			return projectStore.openedProject?.JobAccess ?? 2;
		}
		return 2;
	});

	const isJobsReadonly = computed(() => jobAccessLevel.value === 1);
	const isJobsDenied = computed(() => jobAccessLevel.value === 0);

	const deniedText = computed(() =>
		route.query.page === "project"
			? "У вас отсутствует доступ к работам проекта"
			: "У вас отсутствует доступ к работам клиента"
	);

	return { jobAccessLevel, isJobsReadonly, isJobsDenied, deniedText };
}
